import {
  CREATOR_APPLICATION_ABOUT_MAX_LENGTH,
  CREATOR_APPLICATION_EMAIL_MAX_LENGTH,
  CREATOR_APPLICATION_MAX_INTEREST_AREAS,
  CREATOR_APPLICATION_PORTFOLIO_URL_MAX_LENGTH,
  CREATOR_APPLICATION_SHORT_TEXT_MAX_LENGTH,
  creatorSignatureMatchesName,
  withDefaultScheme,
} from "@newform/constants";

/**
 * Shape and validation for the public creator application.
 *
 * Split out from the form components so the rules are testable without
 * rendering, and so the section cards can stay presentational. These checks
 * mirror `creatorApplicationInputSchema` on the backend — the server is still
 * the authority, this is just so an applicant hears about a problem before
 * they submit rather than after.
 */

// Re-exported from the shared package rather than re-declared: the server
// validates against these exact values, so a local copy would silently cap the
// chips below the schema (or let the form accept input zod then rejects with a
// raw schema message).
export const MAX_INTEREST_AREAS = CREATOR_APPLICATION_MAX_INTEREST_AREAS;
export const MAX_ABOUT_LENGTH = CREATOR_APPLICATION_ABOUT_MAX_LENGTH;
export const MAX_SHORT_TEXT_LENGTH = CREATOR_APPLICATION_SHORT_TEXT_MAX_LENGTH;
export const MAX_EMAIL_LENGTH = CREATOR_APPLICATION_EMAIL_MAX_LENGTH;
export const MAX_PORTFOLIO_URL_LENGTH =
  CREATOR_APPLICATION_PORTFOLIO_URL_MAX_LENGTH;

export type ApplyFormState = {
  name: string;
  email: string;
  location: string;
  ageRange: string;
  tiktokHandle: string;
  instagramHandle: string;
  portfolioUrl: string;
  interestAreas: string[];
  aboutYou: string;
  signatureName: string;
};

export type ApplyFormErrorKey = keyof ApplyFormState | "agreed";

/**
 * Keys into the `apply` catalogue rather than English, so this module stays
 * free of copy and the component translates at render. Every member must exist
 * in `messages/en/apply.json`; the `t()` call in ApplyFlow fails the type check
 * if one doesn't.
 */
export type ApplyValidationKey =
  | "validation.nameRequired"
  | "validation.emailInvalid"
  | "validation.ageRangeRequired"
  | "validation.ageRangeUnknown"
  | "validation.workRequired"
  | "validation.portfolioInvalid"
  | "validation.interestsRequired"
  | "validation.interestsUnknown"
  | "validation.agreementRequired"
  | "validation.signatureRequired"
  | "validation.signatureMismatch";

export type ApplyFormErrors = Partial<
  Record<ApplyFormErrorKey, ApplyValidationKey>
>;

export const EMPTY_APPLY_FORM: ApplyFormState = {
  name: "",
  email: "",
  location: "",
  ageRange: "",
  tiktokHandle: "",
  instagramHandle: "",
  portfolioUrl: "",
  interestAreas: [],
  aboutYou: "",
  signatureName: "",
};

/**
 * Mirrors the server's portfolio rule, including reading `katieugc.com` as
 * `https://katieugc.com`. `type="url"` alone accepts anything with a scheme,
 * including `javascript:`, and browsers disagree about what counts — so the
 * check that matters happens here and again on the server.
 */
export function looksLikeHttpUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(withDefaultScheme(raw));
  } catch {
    return false;
  }
  return (
    (parsed.protocol === "http:" || parsed.protocol === "https:") &&
    parsed.hostname.includes(".")
  );
}

/**
 * Re-exported rather than reimplemented: the server enforces this exact rule,
 * and a second copy would drift the moment either side's normalization changed
 * — reopening the client/server disagreement this module exists to prevent.
 */
export const namesMatch = creatorSignatureMatchesName;

/**
 * `options` are the lists `getAgreement` returned. Checking against them —
 * rather than only checking non-empty — is what makes the narrowing at the
 * submit call site sound: by the time the mutation runs, every value has been
 * matched against the set the server will validate with.
 */
export function validateApplyForm(
  form: ApplyFormState,
  agreed: boolean,
  options?: {
    ageRanges: readonly string[];
    interestAreas: readonly string[];
  },
): ApplyFormErrors {
  const errors: ApplyFormErrors = {};

  if (!form.name.trim()) {
    errors.name = "validation.nameRequired";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "validation.emailInvalid";
  }
  if (!form.ageRange) {
    errors.ageRange = "validation.ageRangeRequired";
  } else if (options && !options.ageRanges.includes(form.ageRange)) {
    errors.ageRange = "validation.ageRangeUnknown";
  }
  // Any one of the three. Shown on the portfolio field because it heads the
  // step, so the message sits above both handles it also refers to.
  if (
    !form.portfolioUrl.trim() &&
    !form.tiktokHandle.trim() &&
    !form.instagramHandle.trim()
  ) {
    errors.portfolioUrl = "validation.workRequired";
  } else if (form.portfolioUrl.trim() && !looksLikeHttpUrl(form.portfolioUrl)) {
    errors.portfolioUrl = "validation.portfolioInvalid";
  }
  if (form.interestAreas.length === 0) {
    errors.interestAreas = "validation.interestsRequired";
  } else if (
    options &&
    !form.interestAreas.every((area) => options.interestAreas.includes(area))
  ) {
    errors.interestAreas = "validation.interestsUnknown";
  }
  if (!agreed) {
    errors.agreed = "validation.agreementRequired";
  }
  if (!form.signatureName.trim()) {
    errors.signatureName = "validation.signatureRequired";
  } else if (!namesMatch(form.signatureName, form.name)) {
    errors.signatureName = "validation.signatureMismatch";
  }

  return errors;
}

/** Toggles an area, refusing to exceed the cap rather than silently dropping. */
export function toggleInterestArea(areas: string[], area: string): string[] {
  if (areas.includes(area)) {
    return areas.filter((a) => a !== area);
  }
  if (areas.length >= MAX_INTEREST_AREAS) {
    return areas;
  }
  return [...areas, area];
}

/**
 * Server messages on this page are written for applicants — but /join is an
 * unauthenticated surface and there is no tRPC errorFormatter redacting
 * INTERNAL_SERVER_ERROR text, so an unexpected throw would echo internals to
 * the public internet. Only the codes this flow deliberately raises get their
 * message shown; anything else degrades to something safe.
 *
 * Lives here rather than in the component so the boundary can be tested
 * directly — it is the only thing standing between a stack-trace-flavoured
 * message and an anonymous visitor.
 */
const SHOWN_ERROR_CODES = new Set([
  "BAD_REQUEST",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "TOO_MANY_REQUESTS",
]);

/** Key into the `apply` catalogue for anything the server's text can't be shown for. */
export const GENERIC_SUBMIT_ERROR = "errors.genericSubmit";

/**
 * Either the server's own message, which is applicant-facing already and
 * shown as sent, or a catalogue key for the component to translate.
 */
export type ApplicantFacingError =
  | { message: string }
  | { key: typeof GENERIC_SUBMIT_ERROR };

export function applicantFacingError(error: {
  message: string;
  data?: { code?: string } | null;
}): ApplicantFacingError {
  return error.data?.code && SHOWN_ERROR_CODES.has(error.data.code)
    ? { message: error.message }
    : { key: GENERIC_SUBMIT_ERROR };
}

/**
 * What the outcome screen shows. `auto_rejected` is not a server outcome of
 * its own: it is a `received` whose row was stored already rejected, split out
 * here because the screen for it says the opposite of "a person reads it".
 * `blocked` is an address staff closed after rejecting it; the screen says the
 * decision is final and offers nothing to fix.
 */
export type SubmitOutcome =
  | "received"
  | "auto_rejected"
  | "already_applied"
  | "already_a_creator"
  | "blocked";

/**
 * The part of `SubmitCreatorApplicationResult` the lead rule needs.
 *
 * Narrower than the backend type on purpose — this asks for what it reads, so
 * `reapplied` and anything added later stay out of it. The mirror does not
 * rely on anyone remembering to update it either way: ApplyFlow hands
 * `shouldTrackCreatorApplication` the tRPC-inferred result directly, so dropping or
 * renaming `isTest` on the backend fails the frontend build at that call.
 */
export type SubmitResult =
  | {
      outcome: "received";
      isTest: boolean;
      autoRejected: { problems: HandleProblem[] } | null;
      metaApplicationEventId: string;
    }
  | { outcome: "already_applied" }
  | { outcome: "already_a_creator" }
  | { outcome: "blocked" };

/** One handle the server couldn't find a profile behind, and why. */
export type HandleProblem = {
  platform: "tiktok" | "instagram";
  value: string;
  problem: "not_a_handle" | "no_such_profile";
};

export function submitOutcomeOf(result: SubmitResult): SubmitOutcome {
  return result.outcome === "received" && result.autoRejected
    ? "auto_rejected"
    : result.outcome;
}

/**
 * Whether a finished submit counts as an application for Meta: the pixel's
 * `SubmitApplication`, and its legacy `Lead` until the ad sets move off it.
 * The iOS app applies the same rule in `MetaAppEvents.swift`.
 *
 * Two ways to get this wrong, both of which quietly corrupt the ad account
 * rather than break anything visible:
 *
 * - `already_applied` / `already_a_creator` are the same person arriving a
 *   second time. No row entered the review queue, so counting them would
 *   understate cost per lead and train Meta's optimisation to find people who
 *   have already applied.
 * - Staff rehearse the funnel through the staff-preview path, which stores the
 *   application as a test. Those runs are ours, not the ad's.
 * - An auto-rejected application (no working TikTok or Instagram) never
 *   reaches the queue, and counting it would teach Meta to find more of them.
 * - A blocked address wrote nothing at all.
 *
 * `received` covers a re-application after a rejection, which is counted on
 * purpose: a row really did enter the queue, and it really did come from the
 * ad that brought them back.
 *
 * Takes the whole result rather than an outcome plus a locally-inferred flag.
 * Staff preview is recomputed per request on the server, so `getAgreement`'s
 * cached answer can disagree with the row `submit` actually wrote — if the
 * funnel is opened or closed while someone has the form up. `isTest` comes
 * back from the stored row, which is the only thing that cannot drift.
 */
export function shouldTrackCreatorApplication(result: SubmitResult): boolean {
  return (
    result.outcome === "received" && !result.isTest && !result.autoRejected
  );
}
